import {useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger} from '@/components/ui/dropdown-menu'
import {useExportRevisionXlsx, useImportRevisionXlsx, type ImportRevision} from '@/hooks/bateria/use-bateria-cola'
import {translateApiError} from '@/lib/errors'

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

/** G31 (v2 §5.2). Importar reporta siempre lo que quedó sin aplicar (`ignoradas`), nunca en silencio. */
export function RevisionXlsxControl({
  businessId,
  evaluacionId,
  selectedPaqueteIds,
  readOnly,
}: {
  businessId: string
  evaluacionId: string
  selectedPaqueteIds: string[]
  readOnly: boolean
}) {
  const {t} = useTranslation()
  const exportar = useExportRevisionXlsx(businessId, evaluacionId)
  const importar = useImportRevisionXlsx(businessId, evaluacionId)
  const inputRef = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [resultado, setResultado] = useState<ImportRevision | null>(null)

  function exportarXlsx() {
    setError(null)
    setResultado(null)
    exportar.mutate(selectedPaqueteIds, {onError: (err) => setError(translateApiError(err))})
  }

  function subir(file: File | undefined) {
    if (!file) return
    setError(null)
    setResultado(null)
    importar.mutate(file, {onSuccess: setResultado, onError: (err) => setError(translateApiError(err))})
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="outline" data-testid="revision-xlsx-kebab">
              {t('admin.bateria.revisionXlsx.menu')}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem data-testid="revision-xlsx-exportar" onSelect={exportarXlsx}>
              {selectedPaqueteIds.length > 0
                ? t('admin.bateria.revisionXlsx.exportarSeleccion', {count: selectedPaqueteIds.length})
                : t('admin.bateria.revisionXlsx.exportar')}
            </DropdownMenuItem>
            {!readOnly && (
              <DropdownMenuItem data-testid="revision-xlsx-importar" onSelect={() => inputRef.current?.click()}>
                {t('admin.bateria.revisionXlsx.importar')}
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        {importar.isPending && (
          <span className="text-xs text-muted-foreground" data-testid="revision-xlsx-subiendo">
            {t('admin.bateria.revisionXlsx.subiendo')}
          </span>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={XLSX_MIME}
        className="hidden"
        data-testid="revision-xlsx-archivo"
        onChange={(e) => {
          subir(e.target.files?.[0])
          e.target.value = ''
        }}
      />
      {resultado && (
        <div className="text-xs text-muted-foreground" data-testid="revision-xlsx-resultado">
          <p>
            {t('admin.bateria.revisionXlsx.resultado', {
              aplicadas: resultado.aplicadas,
              conflictos: resultado.conflictos.length,
              ignoradas: resultado.ignoradas.length,
            })}
          </p>
          {resultado.ignoradas.length > 0 && (
            <ul className="mt-1 max-h-32 space-y-0.5 overflow-y-auto" data-testid="revision-xlsx-ignoradas">
              {resultado.ignoradas.map((fila) => (
                <li key={fila.ref} className="font-mono text-[11px]">
                  {fila.ref} · {t(`admin.bateria.revisionXlsx.ignorada.${fila.motivo}`, {defaultValue: fila.motivo})}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {error && <span className="text-xs text-destructive">{error}</span>}
    </div>
  )
}
